import { Text, View, StyleSheet } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import { AiAssignmentResponseDto } from "@packages/api/madlenAPI.schemas";
import MarkdownRenderer from "../custom-markdown";
import PdfLayout from "../pdf/PdfLayout";
import { commonStyles } from "../pdf/CommonPdfStyles";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
interface AiAssignmentCardPDFProps {
  assignment: AiAssignmentResponseDto & { imageUrl: string | null };
  customizationSettings: PdfCustomizationSettings;
}

const styles = StyleSheet.create({
  metadata: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
    flexWrap: "wrap",
  },
  metaItem: {
    fontSize: 10,
    color: "#4B5563", // gray-600
    marginBottom: 8,
    width: "50%",
  },
  section: {
    marginBottom: 20,
    padding: 16,
    backgroundColor: "#FFFBEB", // amber-50
    borderRadius: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 10,
    color: "#D97706", // amber-600
    paddingBottom: 5,
    borderBottom: "1 solid #FDE68A", // amber-200
  },
  rubricSection: {
    marginBottom: 10,
  },
  rubricTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#92400E", // amber-800
    marginBottom: 3,
  },
  rubricWeight: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#D97706", // amber-600
    marginBottom: 5,
    marginTop: 8,
  },
  rubricLevel: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#D97706", // amber-600
  },
  itemContainer: {
    marginBottom: 10,
    paddingLeft: 10,
    borderLeft: "2 solid #F59E0B", // amber-500
  },
  itemRowContainer: {
    marginBottom: 10,
    paddingLeft: 10,
    borderLeft: "2 solid #F59E0B", // amber-500
    display: "flex",
    flexDirection: "row",
    gap: 10,
    alignItems: "center",
    justifyContent: "flex-start",
  },
  stepContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 8,
  },
  stepNumber: {
    backgroundColor: "#F59E0B",
    color: "white",
    fontSize: 9,
    fontWeight: "bold",
    padding: 4,
    borderRadius: 10,
    marginRight: 8,
    textAlign: "center",
    width: 25,
    height: 20,
  },
  stepContent: {
    flex: 1,
  },
  footer: {
    marginTop: 20,
    borderTop: "1 solid #EAEAEA",
    paddingTop: 10,
    fontSize: 8,
    color: "#9CA3AF", // gray-400
    textAlign: "center",
  },
});

const AiAssignmentCardPDF = ({
  assignment,
  customizationSettings,
}: AiAssignmentCardPDFProps) => {
  const { t } = useTranslation();
  const sectionTitleStyle = [
    styles.sectionTitle,
    {
      fontSize: scaleFontSize(
        styles.sectionTitle.fontSize,
        customizationSettings?.fontSize,
      ),
    },
  ];
  return (
    <PdfLayout
      courseName={assignment.courseName}
      gradeLevel={assignment.gradeLevel}
      topic={assignment.title || t("aiAssignmentDesigner.title")}
      imageUrl={assignment.imageUrl || undefined}
      headerTextColor="#D97706" // amber-600
      title={assignment.title}
      author={assignment.authorName}
      createdDate={assignment.createdAt}
      subject={assignment.courseName}
      customizationSettings={customizationSettings}
    >
      {assignment.implementationSteps &&
        assignment.implementationSteps.length > 0 && (
          <View style={styles.section} wrap>
            <Text style={sectionTitleStyle}>
              {t("aiAssignmentDesigner.implementationSteps")}
            </Text>
            {assignment.implementationSteps.map((step, idx) => (
              <View key={idx} style={styles.itemContainer}>
                <View style={styles.stepContainer}>
                  <Text style={styles.stepNumber}>{idx + 1}</Text>
                  <View style={styles.stepContent}>
                    <MarkdownRenderer
                      content={step}
                      customStyle={{
                        text: {
                          text: {
                            ...commonStyles.sectionContent,
                            fontSize: scaleFontSize(
                              commonStyles.sectionContent.fontSize,
                              customizationSettings?.fontSize,
                            ),
                          },
                        },
                      }}
                    />
                  </View>
                </View>
              </View>
            ))}
          </View>
        )}
      {assignment.studentInstructions && (
        <View style={styles.section} wrap>
          <Text style={sectionTitleStyle}>
            {t("aiAssignmentDesigner.studentInstructions")}
          </Text>
          <View style={styles.itemContainer}>
            <MarkdownRenderer
              content={assignment.studentInstructions}
              customStyle={{
                text: {
                  text: {
                    ...commonStyles.sectionContent,
                    fontSize: scaleFontSize(
                      commonStyles.sectionContent.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                  container: {
                    marginBottom: 12,
                    paddingVertical: 2,
                  },
                },
                listItem: {
                  text: {
                    ...commonStyles.sectionContent,
                    fontSize: scaleFontSize(
                      commonStyles.sectionContent.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                  container: {
                    marginBottom: 12,
                  },
                },
              }}
            />
          </View>
        </View>
      )}
      {assignment.reflectionQuestions &&
        assignment.reflectionQuestions.length > 0 && (
          <View style={styles.section} wrap>
            <Text style={sectionTitleStyle}>
              {t("aiAssignmentDesigner.reflectionQuestions")}
            </Text>
            {assignment.reflectionQuestions.map((question, idx) => (
              <View key={idx} style={styles.itemContainer}>
                <MarkdownRenderer
                  content={question}
                  customStyle={{
                    text: {
                      text: {
                        ...commonStyles.sectionContent,
                        fontSize: scaleFontSize(
                          commonStyles.sectionContent.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                    },
                  }}
                />
              </View>
            ))}
          </View>
        )}

      {assignment.gradingRubric && assignment.gradingRubric.length > 0 && (
        <View style={styles.section} wrap>
          <Text style={sectionTitleStyle}>
            {t("aiAssignmentDesigner.gradingRubric")}
          </Text>
          {assignment.gradingRubric.map((rubricItem, idx) => (
            <View key={idx} style={styles.rubricSection}>
              <Text
                style={[
                  styles.rubricTitle,
                  {
                    fontSize: scaleFontSize(
                      styles.rubricTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {rubricItem.criterion}
              </Text>
              <Text
                style={[
                  styles.rubricWeight,
                  {
                    fontSize: scaleFontSize(
                      styles.rubricWeight.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("aiAssignmentDesigner.weights")}: {rubricItem.weights}
              </Text>

              {Object.entries(rubricItem.levels || {}).map(
                ([level, description], levelIdx) => (
                  <View key={levelIdx} style={styles.itemRowContainer}>
                    <MarkdownRenderer
                      content={description}
                      customStyle={{
                        text: {
                          text: {
                            ...commonStyles.sectionContent,
                            fontSize: scaleFontSize(
                              commonStyles.sectionContent.fontSize,
                              customizationSettings?.fontSize,
                            ),
                          },
                        },
                      }}
                    />
                  </View>
                ),
              )}
            </View>
          ))}
        </View>
      )}

      {assignment.aiPromptExamples &&
        assignment.aiPromptExamples.length > 0 && (
          <View style={styles.section} wrap>
            <Text style={sectionTitleStyle}>
              {t("aiAssignmentDesigner.aiPromptExamples")}
            </Text>
            {assignment.aiPromptExamples.map((example, idx) => (
              <View key={idx} style={styles.itemContainer}>
                <MarkdownRenderer
                  content={example}
                  customStyle={{
                    text: {
                      text: {
                        ...commonStyles.sectionContent,
                        fontSize: scaleFontSize(
                          commonStyles.sectionContent.fontSize,
                          customizationSettings?.fontSize,
                        ),
                        fontStyle: "italic",
                      },
                    },
                  }}
                />
              </View>
            ))}
          </View>
        )}
    </PdfLayout>
  );
};

export default AiAssignmentCardPDF;
